import { getSignInErrorText } from "./authErrorText";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import useCmpMemberShellClass from "../../common/Hooks/useCmpMemberShellClass";
import {
  ThemeProvider,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import theme from "../../../themes";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import EyeOnIcon from "@material-ui/icons/VisibilityOutlined";
import EyeOffIcon from "@material-ui/icons/VisibilityOffOutlined";
import { signInHandler } from "./SignInHandler";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  useGetChamberForMemberLoginQuery,
  useGetCmpMemberDashboardChamberBySlugQuery,
} from "../../../graphql/types";
import OneChamberSystemLogo from "../../../images/osc_logo.png";
import {
  getSafeNextPath,
  rememberMemberDashboardSlug,
} from "../../common/utilities/memberDashboardHost";

interface LoginInput {
  email: string;
  password: string;
}

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    padding: "40px 16px",
    [theme.breakpoints.down("xs")]: {
      padding: "16px 10px",
    },
  },
  formFields: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "640px",
    background: "transparent",
    padding: 0,
    boxShadow: "none",
    textAlign: "center",
  },
  // CMP Accelerator Member Dashboard (2) login (ACC:1760-1782).
  chamberLogo: {
    display: "block",
    margin: "0 auto 22px auto",
    width: "240px",
    height: "120px",
    maxWidth: "100%",
    objectFit: "contain",
  },
  welcomeTitle: {
    fontFamily: "DM Sans, sans-serif",
    color: "#000000",
    margin: 0,
    textAlign: "center",
  },
  welcomeLine1: {
    display: "block",
    fontSize: "17px",
    fontWeight: 400,
    marginBottom: "8px",
  },
  welcomeLine2: {
    display: "block",
    fontSize: "26px",
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: "18px",
  },
  description: {
    fontFamily: "DM Sans, sans-serif",
    color: "#000000",
    fontSize: "15px",
    lineHeight: 1.6,
    maxWidth: "600px",
    // 28px + the first field group's 20px top margin = the design 48px (ACC:1764).
    margin: "0 auto 28px auto",
  },
  fieldLabel: {
    fontFamily: "DM Sans, sans-serif",
    textAlign: "left",
    fontWeight: 700,
    fontSize: "14px",
    color: "#000000",
    marginBottom: "8px",
  },
  fieldGroup: {
    marginTop: "20px",
  },
  // Design input (ACC:1768, 1773): 15px, padding 14px 16px, #D8D8D8 border, 8px radius.
  textField: {
    backgroundColor: "white !important",
    borderRadius: "8px !important",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "15px !important",
    "& input": { padding: "14px 16px", fontSize: "15px" },
    "& fieldset": { borderColor: "#D8D8D8", borderRadius: "8px" },
  },
  invalidError: {
    textAlign: "center",
    color: "red",
    marginTop: "20px",
  },
  // The app theme moves every TextField down 16px (themes.tsx MuiTextField top); on this
  // page that pushed the taller design inputs over "Forgot Password?".
  fieldRoot: { "&&": { top: 0 } },
  // Design (ACC:1776): 8px group gap + 2px margin under the password box.
  forgotPasswordContainer: {
    textAlign: "right",
    marginTop: "10px",
  },
  forgotPasswordLink: {
    fontFamily: "DM Sans, sans-serif",
    color: "#C6912A",
    textDecoration: "underline",
    fontWeight: 400,
    fontSize: "14px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "34px",
  },
  // Design Log In (ACC:1778): padding 15px 56px, 16px/700, 8px radius.
  loginButton: {
    padding: "15px 56px !important",
    fontSize: "16px !important",
    fontWeight: "700 !important" as any,
    borderRadius: "8px !important",
    textTransform: "none",
    lineHeight: 1.2,
  },
  noCredentialsText: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "15px",
    color: "#000000",
    lineHeight: 1.6,
    margin: "22px auto 0 auto",
    maxWidth: "420px",
  },
  poweredByContainer: {
    marginTop: "56px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  poweredByText: {
    fontFamily: "DM Sans, sans-serif",
    color: "#000000",
    fontSize: "12px",
  },
  poweredByLogos: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  poweredByLogo: {
    width: "150px",
    height: "auto",
    objectFit: "contain",
  },
  loaderContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

// memberdashboard.chambermemberpro.com only (Monday 13301576985). Not in
// design: the bare host and an unknown slug show this text-only page in the
// branded login styling. It has no login form.
export const MEMBER_DASHBOARD_HOST_TEXT = {
  title: "Member Dashboard",
  description:
    "To sign in, use the Member Dashboard link from your chamber. You can find it on your chamber's website under Member Login, or in your Member Dashboard login email.",
  notFound:
    "We could not find a Member Dashboard for this link. Check the link on your chamber's website or in your Member Dashboard login email.",
};

export const MemberDashboardHostLanding = ({
  notFound = false,
}: {
  notFound?: boolean;
}): ReactElement => {
  const classes = useStyles();
  return (
    <ThemeProvider theme={theme}>
      <div className={classes.pageContainer}>
        <div className={classes.formFields}>
          <h1 className={classes.welcomeTitle}>
            {MEMBER_DASHBOARD_HOST_TEXT.title}
          </h1>
          <p className={classes.description}>
            {notFound
              ? MEMBER_DASHBOARD_HOST_TEXT.notFound
              : MEMBER_DASHBOARD_HOST_TEXT.description}
          </p>
          <div className={classes.poweredByContainer}>
            <div className={classes.poweredByLogos}>
              <img
                src={OneChamberSystemLogo}
                className={classes.poweredByLogo}
                alt="One Chamber System"
              />
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

const MemberLogin = ({
  slug = "",
  nextPath = "",
}: {
  /** memberdashboard.chambermemberpro.com/<slug>: the chamber comes from the slug. */
  slug?: string;
  /** App page to open after sign-in (/<slug>/<page>). */
  nextPath?: string;
} = {}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const location = useLocation();
  const [invalidError, setInvalidError] = useState(false);
  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const queryChamberId = useMemo(
    () =>
      slug
        ? ""
        : new URLSearchParams(location.search).get("chamberId")?.trim() || "",
    [location.search, slug]
  );

  const {
    data,
    loading: chamberLoading,
    error: chamberError,
  } = useGetChamberForMemberLoginQuery({
    variables: { chamberId: queryChamberId },
    skip: !queryChamberId,
  });

  const {
    data: slugData,
    loading: slugLoading,
    error: slugError,
  } = useGetCmpMemberDashboardChamberBySlugQuery({
    variables: { slug },
    skip: !slug,
  });
  const slugChamber = slugData?.getCmpMemberDashboardChamberBySlug;
  const chamberId = slug ? slugChamber?.chamberId || "" : queryChamberId;
  const chamber = slug
    ? slugChamber
      ? { name: slugChamber.name, logo: slugChamber.logo }
      : null
    : data?.getChamber;
  const resolvedSlug = slugChamber?.slug || "";

  // Logout and deep links on this browser return to this chamber's login.
  // Design font sizing and bold weight on the member login, as on the member pages (index.css).
  useCmpMemberShellClass(true);

  useEffect(() => {
    if (resolvedSlug) rememberMemberDashboardSlug(resolvedSlug);
  }, [resolvedSlug]);

  const initialValues: LoginInput = {
    email: "",
    password: "",
  };

  const validationSchema = yup.object({
    email: yup.string().required(enumStrings.loginValidation.emailRequired),
    password: yup
      .string()
      .required(enumStrings.loginValidation.passwordRequired),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: LoginInput) => {
      setLoader(true);
      try {
        await signInHandler(
          input.email.trim().toLowerCase(),
          input.password.trim(),
          () => history.push(RedirectUri.newPassword),
          chamberId,
          slug ? getSafeNextPath(nextPath) || undefined : undefined
        );
        setButtonDisabled(true);
      } catch (error) {
        setInvalidError(true);
        setCustomError(getSignInErrorText(error, enumStrings.loginErrorMessages, {
          chamberName: chamber?.name || enumStrings.userFacingErrors.yourChamber,
        }));
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // memberdashboard.chambermemberpro.com/<slug>: an unknown slug has no
  // unbranded login to fall back to on that host.
  if (slug && slugLoading) {
    return (
      <ThemeProvider theme={theme}>
        <div className={classes.loaderContainer}>
          <CircularProgress color="primary" />
        </div>
      </ThemeProvider>
    );
  }
  if (slug && (slugError || !slugChamber)) {
    return <MemberDashboardHostLanding notFound />;
  }

  // no chamber id in the url or the chamber lookup failed... so fall back to the generic login page
  if (!slug && (!chamberId || chamberError || (!chamberLoading && !data?.getChamber))) {
    return <Redirect to={RedirectUri.login} />;
  }

  if (chamberLoading) {
    return (
      <ThemeProvider theme={theme}>
        <div className={classes.loaderContainer}>
          <CircularProgress color="primary" />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.pageContainer}>
        <form className={classes.formFields} onSubmit={formik.handleSubmit}>
          {chamber?.logo ? (
            <img
              src={chamber.logo}
              className={classes.chamberLogo}
              alt={chamber.name}
            />
          ) : null}
          <h1 className={classes.welcomeTitle}>
            <span className={classes.welcomeLine1}>
              {enumStrings.memberLoginText.welcomeTitleLine1}
            </span>
            <span className={classes.welcomeLine2}>
              {enumStrings.memberLoginText.welcomeTitleLine2.replace(
                "{{chamberName}}",
                chamber?.name || ""
              )}
            </span>
          </h1>
          <p className={classes.description}>
            {enumStrings.memberLoginText.description}
          </p>
          {invalidError ? (
            <div className={classes.invalidError}>{customError}</div>
          ) : null}
          <div className={classes.fieldGroup}>
            <div className={classes.fieldLabel}>
              {enumStrings.memberLoginText.emailLabel}
            </div>
            <TextField
              variant="outlined"
              className={classes.fieldRoot}
              InputProps={{
                className: classes.textField,
              }}
              fullWidth
              id="email"
              name="email"
              placeholder={enumStrings.memberLoginText.emailPlaceholder}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>
          <div className={classes.fieldGroup}>
            <div className={classes.fieldLabel}>
              {enumStrings.memberLoginText.passwordLabel}
            </div>
            <TextField
              variant="outlined"
              className={classes.fieldRoot}
              InputProps={{
                className: classes.textField,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={enumStrings.memberLoginText.passwordPlaceholder}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={
                formik.touched.password && Boolean(formik.errors.password)
              }
              helperText={formik.touched.password && formik.errors.password}
            />
          </div>
          <div className={classes.forgotPasswordContainer}>
            <Link
              className={classes.forgotPasswordLink}
              to={RedirectUri.forgotPassword}
            >
              {enumStrings.memberLoginText.forgotPasswordText}
            </Link>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              type="submit"
              size="large"
              color="primary"
              variant="contained"
              className={classes.loginButton}
              disabled={buttonDisabled || loader}
            >
              {loader ? (
                <CircularProgress />
              ) : (
                enumStrings.memberLoginText.loginButton
              )}
            </Button>
          </div>
          {/* Design (ACC:1779): regular text, only the chamber name bold. */}
          <p className={classes.noCredentialsText}>
            {enumStrings.memberLoginText.noCredentialsText
              .split("{{chamberName}}")
              .map((part, index, parts) => (
                <React.Fragment key={index}>
                  {part}
                  {index < parts.length - 1 ? <b>{chamber?.name || ""}</b> : null}
                </React.Fragment>
              ))}
          </p>
          <div className={classes.poweredByContainer}>
            <div className={classes.poweredByText}>
              {enumStrings.memberLoginText.poweredByText}
            </div>
            <div className={classes.poweredByLogos}>
              <img
                src={OneChamberSystemLogo}
                className={classes.poweredByLogo}
                alt="One Chamber System"
              />
            </div>
          </div>
        </form>
      </div>
    </ThemeProvider>
  );
};

export default MemberLogin;
